import SEO from '../components/SEO';
import Schema, { createFAQSchema } from '../components/Schema';
import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Search, ChevronDown, ChevronUp, CheckCircle2, MessageCircle, HelpCircle } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import axios from 'axios';

const FAQPage = () => {
  const [faqs, setFaqs] = useState([]);
  const [categories, setCategories] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [expandedId, setExpandedId] = useState(null);
  const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

  useEffect(() => {
    fetchFAQs();
  }, []);

  const fetchFAQs = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/faqs`);
      setFaqs(response.data.faqs);
      const cats = [...new Set(response.data.faqs.map(f => f.category))];
      setCategories(cats);
    } catch (error) {
      console.error('Error:', error);
    }
  };

  const filteredFAQs = faqs.filter(faq => {
    const matchesCategory = selectedCategory === 'all' || faq.category === selectedCategory;
    const matchesSearch = faq.question.toLowerCase().includes(searchTerm.toLowerCase()) || faq.answer.toLowerCase().includes(searchTerm.toLowerCase());
    return matchesCategory && matchesSearch;
  });

  return (
    <div className="min-h-screen bg-white">
      <SEO 
        title="SAP Business One FAQs | Common Questions Answered"
        description="Frequently asked questions about SAP Business One, implementation, cloud hosting, pricing, and working with Ascarii. Get instant answers from experts."
        keywords="SAP B1 FAQ, SAP Business One questions, ERP FAQ, implementation questions, pricing FAQ, SAP answers"
        canonical="https://www.ascarii.com/faqs"
        breadcrumbs={[
          { name: 'Home', url: '/' },
          { name: 'FAQs' }
        ]}
      />
      <Schema data={createFAQSchema(filteredFAQs)} />

      {/* Hero */}
      <section className="relative min-h-[600px] flex items-center overflow-hidden bg-gradient-to-br from-blue-600 via-sky-700 to-cyan-600">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute inset-0" style={{ backgroundImage: 'linear-gradient(rgba(255, 255, 255, 0.1) 1px, transparent 1px), linear-gradient(90deg, rgba(255, 255, 255, 0.1) 1px, transparent 1px)', backgroundSize: '60px 60px' }} />
        </div>
        <div className="container mx-auto px-4 lg:px-8 py-20 relative z-10">
          <div className="max-w-4xl mx-auto text-center">
            <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}>
              <div className="inline-block px-4 py-2 bg-white/20 backdrop-blur-sm border border-white/30 rounded-full text-sm font-semibold mb-6 text-white">Help Centre</div>
              <h1 className="text-5xl md:text-6xl font-bold text-white mb-6">Frequently Asked Questions</h1>
              <p className="text-xl text-blue-100 mb-8">Find answers to common questions about SAP Business One, implementation, cloud hosting, and working with Ascarii</p>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Stats */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4 lg:px-8">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            {[
              { value: "8", label: "Categories" },
              { value: "15+", label: "Years Expertise" },
              { value: "500+", label: "Implementations" },
              { value: "4.9/5", label: "Support Rating" }
            ].map((stat, i) => (
              <motion.div key={i} initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5, delay: i * 0.1 }} className="text-center">
                <div className="text-4xl md:text-5xl font-bold text-blue-600 mb-2">{stat.value}</div>
                <div className="text-sm text-gray-600 font-medium">{stat.label}</div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Search and Filter */}
      <section className="py-12 bg-gradient-to-b from-gray-50 to-white">
        <div className="container mx-auto px-4 lg:px-8 max-w-4xl">
          <div className="bg-white rounded-xl p-6 border border-gray-200 shadow-lg">
            <div className="grid md:grid-cols-2 gap-4">
              <div className="relative">
                <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                <input type="text" value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} placeholder="Search FAQs..." className="w-full pl-12 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent" />
              </div>
              <Select value={selectedCategory} onValueChange={setSelectedCategory}>
                <SelectTrigger className="h-12">
                  <SelectValue placeholder="All categories" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Categories</SelectItem>
                  {categories.map(cat => (
                    <SelectItem key={cat} value={cat}>{cat}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            {filteredFAQs.length > 0 && (
              <p className="mt-4 text-sm text-gray-600">Showing {filteredFAQs.length} questions</p>
            )}
          </div>
        </div>
      </section>

      {/* FAQ Accordion */}
      <section className="py-12 bg-white">
        <div className="container mx-auto px-4 lg:px-8 max-w-4xl">
          <div className="space-y-4">
            {filteredFAQs.map((faq) => (
              <motion.div key={faq.id} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} transition={{ duration: 0.3 }} viewport={{ once: true }} className="bg-gradient-to-br from-slate-50 to-white rounded-xl border-2 border-slate-200 hover:border-sky-300 shadow-md hover:shadow-lg transition-all">
                <button onClick={() => setExpandedId(expandedId === faq.id ? null : faq.id)} className="w-full p-6 text-left flex items-center justify-between focus:outline-none group">
                  <div className="flex-1 flex items-start gap-4">
                    <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-blue-500/10 to-sky-500/10 flex items-center justify-center flex-shrink-0 group-hover:from-blue-500 group-hover:to-sky-500 transition-all">
                      <HelpCircle className="w-5 h-5 text-blue-600 group-hover:text-white transition-colors" />
                    </div>
                    <div className="flex-1">
                      <span className="inline-block px-3 py-1 bg-blue-100 text-blue-600 rounded-full text-xs font-semibold mb-2">{faq.category}</span>
                      <h3 className="text-lg font-bold text-gray-900 group-hover:text-blue-600 transition-colors">{faq.question}</h3>
                    </div>
                  </div>
                  <div className="ml-4">
                    {expandedId === faq.id ? (
                      <ChevronUp className="w-6 h-6 text-blue-600" />
                    ) : (
                      <ChevronDown className="w-6 h-6 text-gray-400 group-hover:text-blue-600 transition-colors" />
                    )}
                  </div>
                </button>
                {expandedId === faq.id && (
                  <motion.div initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: 'auto' }} exit={{ opacity: 0, height: 0 }} transition={{ duration: 0.3 }} className="px-6 pb-6">
                    <div className="pl-14 pt-4 border-t border-gray-200">
                      <p className="text-gray-700 leading-relaxed">{faq.answer}</p>
                    </div>
                  </motion.div>
                )}
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Still Have Questions */}
      <section className="py-20 bg-gradient-to-b from-gray-50 to-white">
        <div className="container mx-auto px-4 lg:px-8">
          <div className="max-w-3xl mx-auto text-center">
            <div className="bg-white rounded-2xl p-12 border-2 border-blue-200 shadow-xl">
              <div className="w-20 h-20 mx-auto rounded-full bg-gradient-to-br from-blue-600 to-sky-600 flex items-center justify-center mb-6">
                <MessageCircle className="w-10 h-10 text-white" />
              </div>
              <h2 className="text-3xl font-bold text-gray-900 mb-4">Still Have Questions?</h2>
              <p className="text-gray-600 mb-8">Our team is here to help. Get in touch and we'll answer any questions you have about SAP Business One and working with Ascarii.</p>
              <div className="flex flex-wrap justify-center gap-4">
                <a href="/contact" className="inline-flex items-center justify-center rounded-xl bg-blue-600 text-white px-8 py-3 font-semibold hover:bg-blue-700 shadow-lg transition-colors">
                  <MessageCircle className="w-5 h-5 mr-2" />Get in Touch
                </a>
                <a href="/experience" className="inline-flex items-center justify-center rounded-xl border-2 border-blue-600 text-blue-600 px-8 py-3 font-semibold hover:bg-blue-50 shadow-lg transition-colors">
                  Learn About The Ascarii Experience
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default FAQPage;
